import { useController } from "react-hook-form";
import { useEffect, useState } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Info } from "lucide-react";

import { RadioGroupFieldProps } from "./types";

import { RadioGroup } from "@/components/ui/radio-group";
import { BasicTooltip } from "@/components/ui/Tooltip";

const radioGroupFieldStyles = {
  fieldGroup: cva("flex flex-col", {
    variants: {
      size: {
        desktop: "gap-2",
        dialog: "gap-1.5",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
  label: cva("text-gray-900 font-medium", {
    variants: {
      size: {
        desktop: "text-base",
        dialog: "text-sm",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
  radioGroup: cva("flex flex-row", {
    variants: {
      size: {
        desktop: "gap-2",
        dialog: "gap-1.5",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
};

export type RadioGroupFieldStyleProps = VariantProps<typeof radioGroupFieldStyles.fieldGroup>;

export const RadioGroupField = ({
  field,
  form,
  stepId,
  size = "desktop",
}: RadioGroupFieldProps) => {
  const { trigger, watch } = form;
  const [isInitiated, setIsInitiated] = useState(false);
  const fieldsInputs = watch(stepId);
  const isDisplayed = field.showIf ? field.showIf?.(fieldsInputs) : true;
  const {
    field: { onChange, value },
    fieldState: { error, isDirty },
  } = useController({
    rules: {
      required: isDisplayed ? field.required : false,
      validate: val => (isDisplayed ? field.validation?.(val, fieldsInputs) : undefined),
    },
    control: form.control,
    name: `${stepId}.${field.id}`,
  });

  useEffect(() => {
    if (!isDisplayed && value) {
      onChange("");
      setIsInitiated(false);
    }
  }, [isDisplayed, value, onChange]);

  useEffect(() => {
    if (isDisplayed && !isInitiated) {
      setIsInitiated(true);
      trigger(stepId);
    }
  }, [isDisplayed, stepId, trigger, isInitiated]);

  const items =
    typeof field.fieldOptions === "function"
      ? field.fieldOptions(fieldsInputs)
      : field.fieldOptions || [];

  return (
    <div className={radioGroupFieldStyles.fieldGroup({ size })}>
      <div className="flex items-center gap-2">
        <label className={radioGroupFieldStyles.label({ size })}>
          {typeof field.label === "function" ? field.label(fieldsInputs) : field.label}
        </label>
        {field.tooltip && (
          <BasicTooltip content={field.tooltip}>
            <Info className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700" />
          </BasicTooltip>
        )}
      </div>
      <RadioGroup
        className={radioGroupFieldStyles.radioGroup({ size })}
        key={field.id}
        items={items?.map(option => ({
          label: option.label || "",
          value: option.value || "",
          id: option.id || "",
        }))}
        defaultValue={value}
        onValueChange={onChange}
        disabled={field.readOnly}
      />
      {error && isDirty && <p className="text-sm text-red-500">{error.message}</p>}
    </div>
  );
};
